import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy, Print, Refresh, LockReset } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import {
  TeacherClassRoster,
  getTeacherClassRoster,
  regenerateClassCode,
  resetStudentPin,
} from "../../../services/api";

interface Props {
  gradeId: number;
}

/**
 * Class code and PIN state for one class. The teacher hands the code out in
 * the room, prints the class sheet, and resets a PIN when a child forgets it.
 */
const ClassCodePanel: React.FC<Props> = ({ gradeId }) => {
  const { t, i18n } = useTranslation();
  const [roster, setRoster] = useState<TeacherClassRoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyStudent, setBusyStudent] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRoster(await getTeacherClassRoster(gradeId));
    } catch (err: any) {
      setError(err?.response?.data?.message || t("errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [gradeId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRegenerate = async () => {
    if (!window.confirm(t("regenerateClassCodeConfirm"))) return;
    try {
      const { class_code } = await regenerateClassCode(gradeId);
      setRoster((prev) => (prev ? { ...prev, class_code } : prev));
    } catch (err: any) {
      setError(err?.response?.data?.message || t("errorOccurred"));
    }
  };

  const handleResetPin = async (studentId: number) => {
    setBusyStudent(studentId);
    try {
      await resetStudentPin(gradeId, studentId);
      setRoster((prev) =>
        prev
          ? {
              ...prev,
              students: prev.students.map((s) =>
                s.id === studentId ? { ...s, has_pin: false, is_locked: false } : s
              ),
            }
          : prev
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || t("errorOccurred"));
    } finally {
      setBusyStudent(null);
    }
  };

  const handleCopy = () => {
    if (roster?.class_code) navigator.clipboard?.writeText(roster.class_code);
  };

  /** One sheet: the code at the top, a line per student. */
  const handlePrint = () => {
    if (!roster) return;
    const win = window.open("", "_blank", "width=800,height=900");
    if (!win) return;
    const rows = roster.students
      .map((s) =>
        roster.requires_pin
          ? `<tr><td>${s.name}</td><td class="pin"></td></tr>`
          : `<tr><td>${s.name}</td></tr>`
      )
      .join("");
    const dir = i18n.language === "en" ? "ltr" : "rtl";
    win.document.write(`<!doctype html><html dir="${dir}"><head>
      <meta charset="utf-8" />
      <title>${roster.class_name ?? ""}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 32px; }
        h1 { direction: ltr; }
        h1 { font-size: 56px; letter-spacing: 6px; margin: 0 0 8px; }
        h2 { font-size: 20px; font-weight: normal; margin: 0 0 24px; color: #444; }
        table { width: 100%; border-collapse: collapse; font-size: 20px; }
        td { border-bottom: 1px solid #bbb; padding: 14px 8px; }
        .pin { width: 30%; }
      </style></head><body>
      <h1>${roster.class_code ?? ""}</h1>
      <h2>${[roster.class_name, roster.school_name].filter(Boolean).join(" · ")}</h2>
      <table><tbody>${rows}</tbody></table>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  if (loading) return <CircularProgress size={24} sx={{ my: 2 }} />;

  return (
    <Box sx={{ mb: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t("classCode")}
        </Typography>
        <Typography
          sx={{ fontFamily: "monospace", fontSize: 32, letterSpacing: 4 }}
        >
          {roster?.class_code ?? "—"}
        </Typography>
        <Tooltip title={t("copy")}>
          <span>
            <IconButton onClick={handleCopy} disabled={!roster?.class_code}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("regenerateClassCode")}>
          <IconButton onClick={handleRegenerate}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button startIcon={<Print />} onClick={handlePrint} size="small">
          {t("printClassSheet")}
        </Button>
      </Box>

      {!roster?.requires_pin && (
        <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.7 }}>
          {t("classCodeOnlyExplanation")}
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("name")}</TableCell>
            {roster?.requires_pin && (
              <>
                <TableCell>{t("pinStatus")}</TableCell>
                <TableCell align="center">{t("resetPin")}</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {(roster?.students ?? []).map((s) => (
            <TableRow key={s.id}>
              <TableCell>{s.name}</TableCell>
              {roster?.requires_pin && (
                <>
              <TableCell>
                {s.is_locked ? (
                  <Chip size="small" color="error" label={t("pinLocked")} />
                ) : s.has_pin ? (
                  <Chip size="small" color="success" label={t("pinSet")} />
                ) : (
                  <Chip size="small" label={t("pinNotSet")} />
                )}
              </TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={() => handleResetPin(s.id)}
                  disabled={busyStudent === s.id || (!s.has_pin && !s.is_locked)}
                  aria-label={t("resetPin")}
                >
                  <LockReset fontSize="small" />
                </IconButton>
              </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ClassCodePanel;
